import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { UserProfile } from '../../../core/models/auth.models';
import { ImageApiService } from '../../../core/services/image-api.service';
import { NotificationCounterService } from '../../../core/services/notification-counter.service';
import { NotificationWebSocketService } from '../../../core/services/notification-websocket.service';
import { NotificationApiService } from '../../../core/services/notification-api.service';

interface NavLink {
  label: string;
  route: string;
  fragment?: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  menuOpen = false;
  isAuthenticated = false;
  displayName = '';
  logoUrl = 'assets/images/logo.svg';
  notificationCount = 0;

  private currentUrl = '';
  private currentFragment: string | null = null;
  private readonly destroy$ = new Subject<void>();

  navLinks: NavLink[] = [
    { label: 'Accueil', route: '/accueil' },
    { label: 'Devis', route: '/devis' },
    { label: 'Mon espace', route: '/mon-espace' },
    { label: 'Historique', route: '/historique' },
    { label: 'À Propos', route: '/nous-contacter' },
  ];

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly imageApi: ImageApiService,
    private readonly notificationCounter: NotificationCounterService,
    private readonly notificationApi: NotificationApiService,
    notificationWebSocket: NotificationWebSocketService,
  ) {
    this.syncFromRouter();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.syncFromRouter());
  }

  ngOnInit(): void {
    this.authService.isLoggedIn$.pipe(takeUntil(this.destroy$)).subscribe((loggedIn) => {
      this.isAuthenticated = loggedIn;
    });

    this.authService.userProfile$.pipe(takeUntil(this.destroy$)).subscribe((profile: UserProfile | null) => {
      this.displayName = this.authService.getDisplayName(profile);
    });

    this.imageApi
      .getUrlByName('company_logo', this.logoUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        if (url) {
          this.logoUrl = url;
        }
      });

    this.notificationCounter.count$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.notificationCount = count;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  logout(): void {
    this.menuOpen = false;
    this.authService.logout();
  }

  openNotifications(): void {
    this.menuOpen = false;

    if (!this.isAuthenticated) {
      this.router.navigate(['/se-connecter'], {
        queryParams: { returnUrl: '/notifications' },
      });
      return;
    }

    if (this.notificationCount <= this.notificationApi.pageSize) {
      this.notificationCounter.reset();
    }

    this.router.navigate(['/notifications']);
  }

  notificationBadgeLabel(): string {
    return this.notificationCount > 99 ? '99+' : String(this.notificationCount);
  }

  isActive(link: NavLink): boolean {
    const path = this.currentUrl.split('?')[0].split('#')[0];
    const onRoute = path === link.route || path === `${link.route}/`;

    if (link.route === '/devis') {
      return path === '/devis' || path.startsWith('/devis/');
    }

    if (link.route === '/mon-espace') {
      return path === '/mon-espace' || path.startsWith('/mon-espace/');
    }

    if (link.route === '/historique') {
      return path === '/historique' || path.startsWith('/historique/');
    }

    if (!onRoute) {
      return false;
    }

    if (link.fragment) {
      return this.currentFragment === link.fragment;
    }

    if (link.route === '/accueil') {
      return !this.currentFragment;
    }

    return true;
  }

  onNavClick(link: NavLink): void {
    this.menuOpen = false;
    this.currentFragment = link.fragment ?? null;
  }

  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/logo.svg';
  }

  private syncFromRouter(): void {
    this.currentUrl = this.router.url;
    const tree = this.router.parseUrl(this.router.url);
    this.currentFragment = tree.fragment;
  }
}
